import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Button,
  Typography,
  Chip,
  Paper,
  Box,
  Link
} from '@mui/material';
import { utils, writeFile } from 'xlsx';
import { BriefData, BRIEF_FIELDS } from '../types/briefTypes';
import { suggestRelatedContent, SitePage, ProjectUrl } from '../services/supabase';
import { generateRecommendations } from '../services/openai';

const BriefContainer = styled(Paper)`
  padding: 2rem;
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Section = styled.div`
  margin-bottom: 1.5rem;
`;

const ContentSection = styled(Section)`
  background: #ffffff;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
`;

const SectionTitle = styled(Typography)`
  color: #1f2937;
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #e5e7eb;
`;

const ContentCard = styled(Paper)`
  padding: 1.5rem;
  margin-bottom: 1rem;
  background-color: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 0;
`;

const SimilarityChip = styled(Chip)`
  margin-left: auto;
  background-color: #e0301e !important;
  color: #ffffff !important;
  border-radius: 0 !important;
  font-weight: 500;
`;

const StyledTableCell = styled(TableCell)`
  border-color: #e5e7eb !important;
  padding: 1rem !important;
`;

const HeaderCell = styled(StyledTableCell)`
  font-weight: 600 !important;
  background-color: #f9fafb !important;
  color: #111827 !important;
  width: 30%;
`;

const ValueCell = styled(StyledTableCell)`
  background-color: #ffffff !important;
  color: #4b5563 !important;
`;

const ExportButton = styled(Button)`
  margin-top: 1.5rem !important;
  background-color: #e0301e !important;
  &:hover {
    background-color: #e84835 !important;
  }
  &:active {
    background-color: #c41c0b !important;
  }
`;

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const StyledChip = styled(Chip)`
  background-color: #e5e7eb !important;
  color: #111827 !important;
  font-weight: 500 !important;
  
  &.main-kpi {
    background-color: #f3f4f6 !important;
    color: #374151 !important;
  }
  
  &.sub-kpi {
    background-color: #e5e7eb !important;
    color: #4b5563 !important;
  }
`;

const StyledLink = styled(Link)`
  color: #e0301e !important;
  text-decoration: none !important;
  font-weight: 500;
  &:hover {
    text-decoration: underline !important;
  }
`;

const ContentTypeIcon = styled.span`
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  margin-right: 0.75rem;
  background-color: #e0301e;
  color: #ffffff;
`;

interface RelatedContentState {
  similarContent: SitePage[];
  relatedUrls: ProjectUrl[];
}

interface Props {
  briefData: BriefData;
}

interface SearchParams {
  campaignName: string;
  primaryObjective: string;
  keyMessages: string;
  targetAudience: string;
}

const MarketingBrief: React.FC<Props> = ({ briefData }) => {
  const [relatedContent, setRelatedContent] = useState<RelatedContentState>({
    similarContent: [],
    relatedUrls: []
  });
  const [recommendations, setRecommendations] = useState<string[]>([]);

  const hasEnoughInfo = useCallback(() => {
    return briefData.campaignType && briefData.primaryObjective && briefData.targetAudience;
  }, [briefData.campaignType, briefData.primaryObjective, briefData.targetAudience]);

  const fetchRecommendations = useCallback(async () => {
    if (hasEnoughInfo()) {
      const suggestions = await generateRecommendations(briefData);
      setRecommendations(suggestions);
    }
  }, [briefData, hasEnoughInfo]);

  useEffect(() => {
    fetchRecommendations();
  }, [fetchRecommendations]);

  useEffect(() => {
    const fetchContent = async () => {
      if (briefData.campaignName?.trim() || briefData.keyMessages?.trim()) {
        try {
          const searchParams: SearchParams = {
            campaignName: briefData.campaignName || '',
            primaryObjective: briefData.primaryObjective || '',
            keyMessages: briefData.keyMessages || '',
            targetAudience: briefData.targetAudience || ''
          };
          const content = await suggestRelatedContent(searchParams);
          setRelatedContent(content);
        } catch (error) {
          console.error('Error fetching related content:', error);
        }
      } else {
        setRelatedContent({
          similarContent: [],
          relatedUrls: []
        });
      }
    };
    
    fetchContent();
  }, [briefData.campaignName, briefData.keyMessages, briefData.primaryObjective, briefData.targetAudience]);

  const handleExport = () => {
    const ws = utils.json_to_sheet([briefData]);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Marketing Brief');
    writeFile(wb, 'marketing_brief.xlsx');
  };

  const renderFieldValue = (fieldKey: keyof BriefData) => {
    const field = BRIEF_FIELDS.find(f => f.key === fieldKey);
    const value = briefData[fieldKey];

    if (!value) return '...';

    if (field?.type === 'picklist') {
      const values = value.split(',').map(v => v.trim());
      return (
        <ChipsContainer>
          {values.map((v, i) => {
            let chipClassName = '';
            if (v.startsWith('Główne KPI')) chipClassName = 'main-kpi';
            if (v.startsWith('Webinar KPI')) chipClassName = 'sub-kpi';
            
            return (
              <StyledChip 
                key={i} 
                label={v} 
                size="small" 
                className={chipClassName}
              />
            );
          })}
        </ChipsContainer>
      );
    }

    return value;
  };

  return (
    <BriefContainer>
      <ContentSection>
        <SectionTitle>Marketing Brief</SectionTitle>
        <TableContainer>
          <Table>
            <TableBody>
              {BRIEF_FIELDS.map(field => (
                <TableRow key={field.key}>
                  <HeaderCell>{field.label}</HeaderCell>
                  <ValueCell>{renderFieldValue(field.key)}</ValueCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <ExportButton variant="contained" onClick={handleExport}>
          Export to Excel
        </ExportButton>
      </ContentSection>

      {recommendations.length > 0 && (
        <ContentSection>
          <SectionTitle>AI Recommendations</SectionTitle>
          {recommendations.map((rec, index) => (
            <Typography key={index} variant="body1" gutterBottom>
              {rec}
            </Typography>
          ))}
        </ContentSection>
      )}

      {(relatedContent.similarContent.length > 0 || relatedContent.relatedUrls.length > 0) && (
        <ContentSection>
          <SectionTitle>Internal Knowledge Base</SectionTitle>
          <Typography variant="subtitle1" color="textSecondary" gutterBottom>
            Related content from our database
          </Typography>
          
          {relatedContent.similarContent.map((page, index) => (
            <ContentCard key={index} elevation={0}>
              <Box display="flex" alignItems="center" marginBottom={1}>
                <ContentTypeIcon>{page.type}</ContentTypeIcon>
                {page.similarity && (
                  <SimilarityChip
                    label={`${Math.round(page.similarity * 100)}% match`}
                    size="small"
                  />
                )}
              </Box>
              <StyledLink href={page.url} target="_blank" rel="noopener noreferrer">
                {page.title}
              </StyledLink>
              {page.description && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  {page.description}
                </Typography>
              )}
            </ContentCard>
          ))}
        </ContentSection>
      )}
    </BriefContainer>
  );
};

export default MarketingBrief;